import styled from "styled-components";

/* ─── Page ───────────────────────────────────────────────────── */

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const PageTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #111;
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #18a860;
  }
`;

/* ─── Toolbar ────────────────────────────────────────────────── */

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  max-width: 320px;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #bbb;
  pointer-events: none;
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px 0 36px;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  font-size: 14px;
  color: #111;
  background-color: #fff;
  transition: border-color 0.15s;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: #1fbc70;
  }
`;

export const CountText = styled.p`
  font-size: 13px;
  color: #aaa;
  margin-left: auto;
`;

/* ─── Table ──────────────────────────────────────────────────── */

export const Card = styled.div`
  background-color: #fff;
  border-radius: 16px;
  border: 1px solid #eaeaea;
  overflow: hidden;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Thead = styled.thead`
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;

export const Th = styled.th<{ width?: string }>`
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-align: left;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  width: ${({ width }) => width ?? "auto"};
`;

export const Tbody = styled.tbody``;

export const Tr = styled.tr`
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fafff9;
  }
`;

export const Td = styled.td`
  padding: 14px 20px;
  font-size: 14px;
  color: #333;
  vertical-align: middle;
`;

export const IndexCell = styled.span`
  font-size: 13px;
  color: #bbb;
  font-weight: 500;
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
`;

export const EditButton = styled.button`
  height: 32px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: #1fbc70;
  background-color: #f0fdf7;
  border: 1px solid #c6f0dc;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s;

  &:hover {
    background-color: #dcfaed;
  }
`;

export const DeleteButton = styled.button`
  height: 30px;
  padding: 0 12px;
  border: 1px solid #f0d0d0;
  border-radius: 7px;
  background-color: #fff5f5;
  color: #fc324a;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #fde8e8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const EmptyRow = styled.tr``;

export const EmptyCell = styled.td`
  padding: 60px 20px;
  text-align: center;
  color: #bbb;
  font-size: 14px;
`;

/* ─── Modal overlay ──────────────────────────────────────────── */

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

export const Modal = styled.div`
  background-color: #fff;
  border-radius: 20px;
  padding: 36px 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 48px rgba(0, 0, 0, 0.15);
`;

export const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
  margin-bottom: 24px;
`;

export const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
`;

export const ModalInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1.5px solid ${({ hasError }) => (hasError ? "#fc324a" : "#e8e8e8")};
  border-radius: 10px;
  font-size: 15px;
  color: #111;
  background-color: #fafafa;
  transition: border-color 0.15s;
  margin-bottom: 6px;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? "#fc324a" : "#1fbc70")};
    background-color: #fff;
  }
`;

export const ModalError = styled.p`
  font-size: 12px;
  color: #fc324a;
  margin-bottom: 4px;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 28px;
`;

export const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #666;
  background-color: #fff;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

export const SaveButton = styled.button`
  flex: 1;
  height: 48px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background-color: #1fbc70;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #18a860;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;
